import { EntityType } from "@common/Common.types";
import { FilterOperatorUi, ListFilter, ListFilterOperator } from "@common/api/filterTypes";
import { TranslationStringType } from "@localization/LocalizationContext";
import { IdNamePair } from "../customLists/customListView/CustomListView.types";
import { FrameworkControlStatus } from "../framework/frameworks/Frameworks.types";
import { entityTypeLabel } from "./riskOperationalOverview/RiskOperationalOverview.types";

// Ja/Nej filters are modelled as a single-select list ("true"/"false"); none or both selected = no filter.
export function toBoolFilter(values: Array<string>): boolean | undefined {
    return values.length === 1 ? values[0] === "true" : undefined;
}

// The reverse of toBoolFilter for the filter-bar display: a boolean filter shows as a single-element selection.
export function fromBoolFilter(value: boolean | undefined): Array<string> {
    return value === undefined ? [] : [value ? "true" : "false"];
}

export function yesNoFilterOptions(translateString: (key: string) => string): Array<IdNamePair> {
    return [
        { id: "true", name: translateString("yes") },
        { id: "false", name: translateString("no") },
    ];
}

// Risk levels are identified by their 1-based level number, not their position in the label array — asserted
// here once so every risk-level filter across the overviews offers the same ids for the same bands.
export function riskLevelFilterOptions(labels: Array<string> | undefined): Array<IdNamePair> {
    return (labels ?? []).map((label, index) => ({ id: (index + 1).toString(), name: label }));
}

export function frameworkControlFilterOptions(controls: Array<{ id: string; name: string; customId?: string | null }> | undefined): Array<IdNamePair> {
    return (controls ?? []).map((control) => ({
        id: control.id,
        name: control.customId ? `${control.customId} ${control.name}` : control.name,
    }));
}

// Only the statuses the task-derived rollup can actually produce (see FrameworkControlStatusRollup /
// FrameworkControlProgress) are offered; the intermediate approval states never surface as a control status.
export function controlStatusFilterOptions(translateFrameworkControlStatusEnum: (status: string) => string): Array<IdNamePair> {
    return [FrameworkControlStatus.Planned, FrameworkControlStatus.Ongoing, FrameworkControlStatus.Completed, FrameworkControlStatus.Overdue].map((status) => ({
        id: status.toString(),
        name: translateFrameworkControlStatusEnum(status.toString()),
    }));
}

// The Type filter's organisation option. EntityType.Organisation exists, but it says which subjects a risk scenario
// applies to, not which subject an assessment has, and these ids are persisted in saved filter state — so the
// organisation keeps its own id here and is split back out on the way to the request.
export const organisationTypeFilterId = "organisation";

export const assessableEntityTypes = [EntityType.ProcessingActivity, EntityType.System, EntityType.LegalEntity, EntityType.Process];

export function assessableEntityTypeOptions(translateString: TranslationStringType): Array<IdNamePair> {
    return assessableEntityTypes.map((type) => ({ id: type.toString(), name: entityTypeLabel(type, translateString) }));
}

export interface SubjectTypeRequest {
    types?: ListFilter<EntityType>;
    includeOrganisationSubject?: boolean;
}

// The raw Type selection as it is persisted: the entity types ticked, and separately whether the organisation
// option is.
export interface SubjectTypeFilter {
    types?: ListFilter<EntityType>;
    includeOrganisation?: boolean;
}

// Splits one Type selection into the two halves that are stored. Deliberately not the generic toListFilter, which
// drops the whole filter — operator and all — as soon as the value list is empty: here the entity list empties out
// while the filter is very much set, because the organisation rides beside it, and "none of the organisation" is
// exactly that case. So the operator is kept whenever anything at all is ticked, and only a selection with nothing
// in it clears the filter, which is what keeps a cleared filter deep-equal to no filter for change detection.
export function toSubjectTypeFilter(values: Array<string>, operator: FilterOperatorUi | undefined): SubjectTypeFilter {
    const organisationSelected = values.includes(organisationTypeFilterId);
    const entityTypes = values.filter((value) => value !== organisationTypeFilterId).map((value) => Number(value) as EntityType);

    if (entityTypes.length === 0 && !organisationSelected) {
        return { types: undefined, includeOrganisation: undefined };
    }

    return {
        types: {
            value: entityTypes,
            filterOperator: operator === FilterOperatorUi.Negative ? ListFilterOperator.NotIn : ListFilterOperator.In,
        },
        includeOrganisation: organisationSelected ? true : undefined,
    };
}

// Splits the Type filter into the two halves the backend takes: Types narrows the entity population,
// includeOrganisationSubject says whether the organisation's own assessments are in scope. Derived on the way out
// rather than stored, because the derived shape cannot be read back into the popover: "only the organisation" has
// to reach the backend as "no entity type qualifies", and an empty Types list means the opposite there (no
// restriction), so it goes as a negative filter naming every assessable type, which read back would tick them all.
// A selection of the organisation alone arrives here as an empty type list still carrying its operator (see
// toSubjectTypeFilter), which is what tells the two branches below apart.
export function toSubjectTypeRequest(types: ListFilter<EntityType> | undefined, includeOrganisation: boolean | undefined): SubjectTypeRequest {
    const selectedTypes = types?.value ?? [];
    const organisationSelected = includeOrganisation === true;

    // Nothing chosen at all is not a filter: every subject stays in scope.
    if (selectedTypes.length === 0 && !organisationSelected) {
        return {};
    }

    const isNegative = types?.filterOperator === ListFilterOperator.NotIn;

    if (selectedTypes.length > 0) {
        return {
            types: { value: selectedTypes, filterOperator: isNegative ? ListFilterOperator.NotIn : ListFilterOperator.In },
            includeOrganisationSubject: isNegative ? !organisationSelected : organisationSelected,
        };
    }

    // Only the organisation is named: positively that leaves no entity type in scope, negatively it leaves them
    // all in and takes only the organisation out.
    return {
        types: isNegative ? undefined : { value: assessableEntityTypes, filterOperator: ListFilterOperator.NotIn },
        includeOrganisationSubject: !isNegative,
    };
}

// The subjects a risk area can assess, for every Type filter that offers them: the assessable entity
// types plus the organisation as a whole. One list so the governance and mitigation filters can never drift.
export function subjectTypeFilterOptions(translateString: TranslationStringType): Array<IdNamePair> {
    return [...assessableEntityTypeOptions(translateString), { id: organisationTypeFilterId, name: translateString("organization") }];
}
